import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Radio, Navigation, LifeBuoy, MapPin, RefreshCw } from "lucide-react";
import MapView from "@/components/MapView";

export default function LiveRescuers({ data, userLocation, onRefresh, refreshing, categories = [], onHelpClick, disableHelp }) {
  const active = data?.active || [];
  const fallback = data?.fallback || [];
  const catName = (id) => categories.find((c) => c.id === id)?.name;

  const center = active[0]
    ? [active[0].last_location.latitude, active[0].last_location.longitude]
    : userLocation || [39, 35];

  const markers = active.map((a) => ({
    id: a.vehicle_id,
    latitude: a.last_location.latitude,
    longitude: a.last_location.longitude,
    label: `${a.business.name} — ${a.vehicle_name}`,
    isVehicle: true,
  }));

  const CardRow = ({ item, online }) => {
    const biz = item.business;
    const cats = (item.service_category_ids?.length ? item.service_category_ids : biz.category_ids || [])
      .map(catName).filter(Boolean).slice(0, 2);
    const dest = online
      ? `${item.last_location.latitude},${item.last_location.longitude}`
      : biz.latitude && biz.longitude ? `${biz.latitude},${biz.longitude}` : null;
    return (
      <div className="rounded-2xl border border-border p-3 flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${online ? "bg-primary text-[hsl(var(--brand-black))]" : "bg-secondary text-muted-foreground"}`}>
          <LifeBuoy className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-semibold truncate">{biz.name}</div>
            {online ? (
              <Badge className="bg-[hsl(var(--success))] text-white gap-1"><Radio className="w-3 h-3 animate-pulse" /> HATTA</Badge>
            ) : (
              <Badge variant="secondary" className="text-xs">Şu anda hatta değil</Badge>
            )}
            {biz.is_24_7 && <Badge className="bg-primary text-[hsl(var(--brand-black))]">7/24</Badge>}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
            {online && <span>{item.vehicle_name} · {item.vehicle_type}</span>}
            {!online && <span>{biz.district}, {biz.province}</span>}
            {item.distance_km != null && item.distance_km >= 0.05 && (
              <span className="inline-flex items-center gap-1 font-medium text-foreground">
                <MapPin className="w-3 h-3" />
                {item.distance_km < 1 ? `${Math.round(item.distance_km * 1000)} m` : `${item.distance_km} km`}
              </span>
            )}
          </div>
          {cats.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {cats.map((n) => <Badge key={n} variant="secondary" className="text-xs">{n}</Badge>)}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          {online && !disableHelp && (
            <Button
              size="sm"
              onClick={() => onHelpClick?.(item)}
              className="rounded-full text-xs h-8 press bg-primary text-[hsl(var(--brand-black))] hover:bg-primary/90 font-bold"
              data-testid={`live-help-${item.vehicle_id}`}
            >
              <LifeBuoy className="w-3.5 h-3.5 mr-1" /> YARDIM İSTE
            </Button>
          )}
          {(!online || disableHelp) && (
            <Button size="sm" variant="ghost" className="rounded-full text-xs h-8 opacity-60" disabled data-testid={`live-help-disabled-${online ? item.vehicle_id : biz.id}`}>
              {disableHelp ? "Aktif talebiniz var" : "Şu anda hatta değil"}
            </Button>
          )}
          <Button asChild size="sm" variant="outline" className="rounded-full text-xs h-8 press" data-testid={`live-view-${online ? item.vehicle_id : biz.id}`}>
            <Link to={`/firma/${biz.slug || biz.id}`}>Firmayı Gör</Link>
          </Button>
          {dest && (
            <Button asChild size="sm" variant="ghost" className="rounded-full text-xs h-8" data-testid={`live-dir-${online ? item.vehicle_id : biz.id}`}>
              <a href={`https://www.google.com/maps/dir/?api=1&destination=${dest}`} target="_blank" rel="noopener noreferrer">
                <Navigation className="w-3.5 h-3.5 mr-1" /> Yol Tarifi
              </a>
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <section className="mx-auto max-w-6xl px-4 py-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xl sm:text-2xl font-bold tracking-tight flex items-center gap-2">
          <Radio className="w-5 h-5 text-primary" /> Yakınındaki Aktif Kurtarıcılar
        </h2>
        <Button size="sm" variant="ghost" onClick={onRefresh} disabled={refreshing} data-testid="live-refresh">
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
        </Button>
      </div>

      <div className="rounded-2xl overflow-hidden border border-border h-[320px] sm:h-[380px] mb-4">
        <MapView
          center={center}
          zoom={active.length ? 12 : 11}
          markers={markers}
          userLocation={userLocation}
          className="h-full"
        />
      </div>

      <div className="grid gap-2">
        {active.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border p-4 text-sm text-muted-foreground text-center" data-testid="no-active">
            Şu anda yakınınızda hattaki kurtarıcı bulunmuyor. Aşağıda ilinizdeki uygun kurtarıcı işletmeler listelenmiştir.
          </div>
        )}
        {active.map((a) => <CardRow key={a.vehicle_id} item={a} online />)}
      </div>

      {fallback.length > 0 && (
        <div className="mt-6">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            {data?.province ? `${data.province} — Şu anda hatta değil` : "Yakınınızdaki İşletmeler"}
          </h3>
          <div className="grid gap-2">
            {fallback.map((f) => <CardRow key={f.business.id} item={f} online={false} />)}
          </div>
        </div>
      )}
    </section>
  );
}
